import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { normalizeAllowedBehaviors } from "@/lib/appointments/availability";

export type PatientPackageFinancialStatus = "pendente" | "parcial" | "pago";
export type PatientPackageOrigin = "manual" | "appointment";

export interface PatientPackageSummary {
  id: string;
  clinic_id: string;
  customer_id: string;
  pet_id: string;
  catalog_package_id: string;
  sessions_total: number;
  sessions_used: number;
  sessions_remaining: number;
  status_financeiro: PatientPackageFinancialStatus;
  full_price: number;
  amount_paid: number;
  outstanding_amount: number;
  origin: PatientPackageOrigin;
  created_from_appointment_id: string | null;
  created_from_appointment?: {
    id: string;
    starts_at: string | null;
  } | null;
  created_at: string;
  updated_at: string;
  catalog_package: {
    id: string;
    item_id: string;
    controls_sessions: boolean;
    default_sessions: number;
    notes: string | null;
    item: {
      id: string;
      name: string;
      sale_price: number;
      package_subtype_id?: string | null;
      package_subtype?: { name?: string | null } | null;
      allowed_appointment_types: string[];
    } | null;
  } | null;
}

export interface PatientPackageConsumptionSummary {
  id: string;
  appointment_id: string;
  patient_package_id: string | null;
  sessions_to_consume: number;
  consumed_at: string | null;
  appointment: {
    id: string;
    starts_at: string | null;
    status: string | null;
  } | null;
}

type RawPackageItem = {
  id: string;
  name: string;
  sale_price: number | null;
  package_subtype_id?: string | null;
  package_subtype?: { name?: string | null } | null;
  allowed_appointment_types?: Array<{ appointment_type?: string | null }> | null;
};

type RawCatalogPackage = {
  id: string;
  item_id: string;
  controls_sessions?: boolean | null;
  default_sessions: number | null;
  notes: string | null;
  item: RawPackageItem | null;
};

type RawPatientPackage = {
  id: string;
  clinic_id: string;
  customer_id: string;
  pet_id: string;
  catalog_package_id: string;
  sessions_total: number | null;
  sessions_used: number | null;
  sessions_remaining: number | null;
  status_financeiro: PatientPackageFinancialStatus | null;
  full_price: number | null;
  amount_paid: number | null;
  outstanding_amount: number | null;
  origin: PatientPackageOrigin | null;
  created_from_appointment_id: string | null;
  created_from_appointment?: {
    id: string;
    starts_at: string | null;
  } | null;
  created_at: string;
  updated_at: string;
  catalog_package: RawCatalogPackage | null;
};

export const normalizePatientPackage = (row: RawPatientPackage): PatientPackageSummary => ({
  id: row.id,
  clinic_id: row.clinic_id,
  customer_id: row.customer_id,
  pet_id: row.pet_id,
  catalog_package_id: row.catalog_package_id,
  sessions_total: row.sessions_total ?? 0,
  sessions_used: row.sessions_used ?? 0,
  sessions_remaining: row.sessions_remaining ?? Math.max((row.sessions_total ?? 0) - (row.sessions_used ?? 0), 0),
  status_financeiro: row.status_financeiro ?? "pendente",
  full_price: row.full_price ?? 0,
  amount_paid: row.amount_paid ?? 0,
  outstanding_amount: row.outstanding_amount ?? 0,
  origin: row.origin ?? "appointment",
  created_from_appointment_id: row.created_from_appointment_id ?? null,
  created_from_appointment: row.created_from_appointment
    ? {
        id: row.created_from_appointment.id,
        starts_at: row.created_from_appointment.starts_at ?? null,
      }
    : null,
  created_at: row.created_at,
  updated_at: row.updated_at,
  catalog_package: row.catalog_package
    ? {
      id: row.catalog_package.id,
      item_id: row.catalog_package.item_id,
      controls_sessions: row.catalog_package.controls_sessions ?? true,
      default_sessions: row.catalog_package.default_sessions ?? 1,
      notes: row.catalog_package.notes ?? null,
      item: row.catalog_package.item
        ? {
          id: row.catalog_package.item.id,
          name: row.catalog_package.item.name,
          sale_price: row.catalog_package.item.sale_price ?? 0,
          allowed_appointment_types: normalizeAllowedBehaviors(
            row.catalog_package.item.allowed_appointment_types?.map((entry) => entry.appointment_type ?? null) ?? [],
          ),
        }
        : null,
    }
    : null,
});

export const PATIENT_PACKAGE_SELECT = `
  id,
  clinic_id,
  customer_id,
  pet_id,
  catalog_package_id,
  sessions_total,
  sessions_used,
  sessions_remaining,
  status_financeiro,
  full_price,
  amount_paid,
  outstanding_amount,
  origin,
  created_from_appointment_id,
  created_from_appointment:appointments!patient_packages_created_from_appointment_id_fkey(
    id,
    starts_at
  ),
  created_at,
  updated_at,
  catalog_package:catalog_packages(
    id,
    item_id,
    controls_sessions,
    default_sessions,
    notes,
    item:items(id, name, sale_price, package_subtype_id, package_subtype:package_subtypes(name), allowed_appointment_types:item_allowed_appointment_types(appointment_type))
  )
`;

interface UsePatientPackagesOptions {
  includeInactive?: boolean;
}

type RawPatientPackageConsumption = {
  id: string;
  appointment_id: string;
  patient_package_id: string | null;
  sessions_to_consume: number | null;
  consumed_at: string | null;
  appointment?: {
    id: string;
    starts_at: string | null;
    status: string | null;
  } | null;
};

export const usePatientPackages = (
  petId?: string | null,
  clinicId?: string | null,
  options?: UsePatientPackagesOptions,
) => {
  const includeInactive = options?.includeInactive ?? false;

  return useQuery({
    queryKey: ["patient-packages", clinicId, petId, includeInactive],
    enabled: Boolean(petId && clinicId),
    queryFn: async (): Promise<PatientPackageSummary[]> => {
      if (!petId || !clinicId) return [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("patient_packages")
        .select(PATIENT_PACKAGE_SELECT)
        .eq("pet_id", petId)
        .eq("clinic_id", clinicId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const normalized = ((data ?? []) as RawPatientPackage[]).map(normalizePatientPackage);
      return includeInactive
        ? normalized
        : normalized.filter((pkg) => !pkg.catalog_package?.controls_sessions || pkg.sessions_remaining > 0);
    },
  });
};

export const usePatientPackageConsumptions = (patientPackageId?: string | null) => {
  return useQuery({
    queryKey: ["patient-package-consumptions", patientPackageId],
    enabled: Boolean(patientPackageId),
    queryFn: async (): Promise<PatientPackageConsumptionSummary[]> => {
      if (!patientPackageId) return [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("appointment_patient_packages")
        .select(`
          id,
          appointment_id,
          patient_package_id,
          sessions_to_consume,
          consumed_at,
          appointment:appointments(
            id,
            starts_at,
            status
          )
        `)
        .eq("patient_package_id", patientPackageId)
        .eq("consumption_status", "consumed")
        .order("consumed_at", { ascending: false });

      if (error) throw error;

      return ((data ?? []) as RawPatientPackageConsumption[]).map((row) => ({
        id: row.id,
        appointment_id: row.appointment_id,
        patient_package_id: row.patient_package_id ?? null,
        sessions_to_consume: Math.max(1, Number(row.sessions_to_consume ?? 1)),
        consumed_at: row.consumed_at ?? null,
        appointment: row.appointment
          ? {
              id: row.appointment.id,
              starts_at: row.appointment.starts_at ?? null,
              status: row.appointment.status ?? null,
            }
          : null,
      }));
    },
  });
};
